/**
 * Browser + API cache for Schedule → Model Activity ID matches.
 * Server is the shared copy; localStorage keeps a half-finished scan on this machine.
 */

import { hydrateActivityMap, serializeActivityMap, countActivityMap } from './progressTheming';

const PREFIX = 'lgl:schedule4d:activity-map:v1:';

export function normalizeApsUrn(urn) {
  return String(urn || '').replace(/^urn:/i, '').trim();
}

export function activityMapStorageKey(jobNumber, urn) {
  return `${PREFIX}${String(jobNumber || '').trim()}|${normalizeApsUrn(urn)}`;
}

function emptyRecord(urn) {
  return {
    found: false,
    urn: normalizeApsUrn(urn),
    map: new Map(),
    complete: false,
    mapped: 0,
    unmapped: 0,
    activityCount: 0,
    updatedAt: null,
    source: null,
  };
}

function recordFromPayload(payload, source) {
  const map = hydrateActivityMap(payload?.activityMap);
  const counts = countActivityMap(map);
  return {
    found: map.size > 0 || payload?.found === true,
    urn: normalizeApsUrn(payload?.urn),
    map,
    complete: payload?.complete === true,
    mapped: payload?.mapped != null ? Number(payload.mapped) : counts.mapped,
    unmapped: payload?.unmapped != null ? Number(payload.unmapped) : counts.unmapped,
    activityCount: payload?.activityCount != null ? Number(payload.activityCount) : counts.activityCount,
    updatedAt: payload?.updatedAt || null,
    source,
  };
}

export function readLocalActivityMap(jobNumber, urn) {
  try {
    const raw = window.localStorage.getItem(activityMapStorageKey(jobNumber, urn));
    if (!raw) return emptyRecord(urn);
    return recordFromPayload(JSON.parse(raw), 'local');
  } catch {
    return emptyRecord(urn);
  }
}

export function writeLocalActivityMap(jobNumber, urn, byActivity, extras = {}) {
  const activityMap = serializeActivityMap(byActivity);
  const counts = countActivityMap(byActivity);
  const payload = {
    found: true,
    urn: normalizeApsUrn(urn),
    activityMap,
    complete: extras.complete === true,
    mapped: counts.mapped,
    unmapped: counts.unmapped,
    activityCount: counts.activityCount,
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(activityMapStorageKey(jobNumber, urn), JSON.stringify(payload));
  } catch {
    // Quota or private mode — in-memory map still works for this Open.
  }
  return payload;
}

export function clearLocalActivityMap(jobNumber, urn) {
  try {
    window.localStorage.removeItem(activityMapStorageKey(jobNumber, urn));
  } catch {
    /* ignore */
  }
}

export { emptyRecord, recordFromPayload, serializeActivityMap, hydrateActivityMap, countActivityMap };
